/**
 * ProfileVerify — "Verify a Profile" section for the landing page.
 *
 * Users enter a numeric profile ID (the "SP-" prefix is shown as decoration).
 * The component calls GET /verify_profile/:id and shows:
 *   - Found & verified: green confirmation card with basic info
 *   - Not found: a gentle "not found" message
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { HiOutlineShieldCheck } from "react-icons/hi";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import baseURL from "../../authComponents/baseURL/BaseURL";

const ProfileVerify = () => {
  const [profileId, setProfileId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { found, data, message }
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();
  const sectionRef = useRef(null);

  // Auto-scroll to this section when the URL contains #verify-profile
  useEffect(() => {
    if (window.location.hash === "#verify-profile" && sectionRef.current) {
      // Small delay to ensure the page has fully rendered
      const timer = setTimeout(() => {
        sectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleVerify = useCallback(async () => {
    const id = profileId.trim().replace(/^SP-/i, "");
    if (!id || !/^\d+$/.test(id)) return;

    setLoading(true);
    setResult(null);
    setHasSearched(true);

    try {
      const response = await baseURL.get(`/verify_profile/${id}`);
      setResult({ found: true, data: response.data.data });
    } catch (err) {
      if (err.response?.status === 404) {
        setResult({ found: false, message: "No active profile found with this ID." });
      } else {
        setResult({ found: false, message: "Something went wrong. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleVerify();
  };

  return (
    <section
      ref={sectionRef}
      id="verify-profile"
      className="py-16 px-5"
      style={{
        background: "linear-gradient(180deg, #ffffff 0%, #fdfbf9 100%)",
      }}
    >
      <div className="max-w-2xl mx-auto text-center">
        {/* Icon */}
        <div
          className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, rgba(139,30,30,0.08) 0%, rgba(212,168,67,0.08) 100%)",
          }}
        >
          <HiOutlineShieldCheck className="w-8 h-8 text-[#8B1E1E]" />
        </div>

        {/* Title */}
        <h2
          className="font-playfair text-3xl md:text-[2.2rem] font-bold mb-3"
          style={{ color: "#1a1a2e" }}
        >
          Verify a Profile
        </h2>
        <p
          className="text-gray-500 text-base mb-8 max-w-md mx-auto"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Received a profile card? Enter the profile ID to verify it's
          genuine and active on Select Proposal.
        </p>

        {/* Search box */}
        <div className="flex items-stretch max-w-md mx-auto mb-6">
          {/* SP- prefix label */}
          <div
            className="flex items-center justify-center px-4 rounded-l-xl border border-r-0 text-sm font-bold select-none"
            style={{
              background: "#f8f4f0",
              borderColor: "#e0d8d0",
              color: "#8B1E1E",
              fontFamily: "'Inter', sans-serif",
              letterSpacing: 1,
            }}
          >
            SP-
          </div>
          {/* Input */}
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter profile number"
            value={profileId}
            onChange={(e) => {
              // Allow only digits
              const val = e.target.value.replace(/\D/g, "");
              setProfileId(val);
              if (hasSearched) {
                setHasSearched(false);
                setResult(null);
              }
            }}
            onKeyDown={handleKeyDown}
            className="flex-1 px-4 py-3.5 border outline-none text-base"
            style={{
              borderColor: "#e0d8d0",
              fontFamily: "'Inter', sans-serif",
              color: "#333",
            }}
          />
          {/* Button */}
          <button
            onClick={handleVerify}
            disabled={loading || !profileId.trim()}
            className="px-6 rounded-r-xl text-white font-semibold text-sm flex items-center gap-2 transition-all duration-200 disabled:opacity-50"
            style={{
              background: loading
                ? "#999"
                : "linear-gradient(135deg, #8B1E1E 0%, #a52a2a 100%)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <>
                <FaSearch className="w-4 h-4" />
                Verify
              </>
            )}
          </button>
        </div>

        {/* Result */}
        {hasSearched && result && (
          <div className="max-w-md mx-auto">
            {result.found ? (
              <div
                className="rounded-xl p-5 text-left animate-fade-in"
                style={{
                  background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
                  border: "1px solid #bbf7d0",
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span
                    className="text-green-700 font-bold text-base"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Profile Found & Verified ✓
                  </span>
                </div>

                <div
                  className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <div>
                    <span className="text-gray-500">Profile ID</span>
                    <p className="font-semibold text-gray-800">SP-{result.data.id}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Gender</span>
                    <p className="font-semibold text-gray-800 capitalize">
                      {result.data.gender === "male" ? "♂ Male" : "♀ Female"}
                    </p>
                  </div>
                  {result.data.age && (
                    <div>
                      <span className="text-gray-500">Age</span>
                      <p className="font-semibold text-gray-800">{result.data.age} yrs</p>
                    </div>
                  )}
                  {result.data.city && (
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-semibold text-gray-800">
                        {result.data.city}
                        {result.data.country ? `, ${result.data.country}` : ""}
                      </p>
                    </div>
                  )}
                  {result.data.created_on && (
                    <div>
                      <span className="text-gray-500">Member Since</span>
                      <p className="font-semibold text-gray-800">{result.data.created_on}</p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate("/login")}
                  className="w-full py-2.5 rounded-lg text-white font-semibold text-sm transition-all duration-200"
                  style={{
                    background: "linear-gradient(135deg, #8B1E1E 0%, #a52a2a 100%)",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Login to View Full Profile →
                </button>
              </div>
            ) : (
              <div
                className="rounded-xl p-5 text-center"
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span
                    className="text-red-700 font-semibold text-base"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Profile Not Found
                  </span>
                </div>
                <p
                  className="text-red-600/70 text-sm"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {result.message} Please check the ID and try again.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfileVerify;
